import React from 'react';
import { Header, flexRender } from '@tanstack/react-table';
import { TableHead } from '@/components/ui/table';
import { ArrowUp, ArrowDown } from 'lucide-react';
import type { Product } from '@/services/productService';
import { useProductstableTranslations } from '@/hooks/useProductstableTranslations';

/**
 * One column header with clickable sort controls.
 * Pure presentational – no local state.
 */
export interface SortableTableHeaderProps {
  id: string;
  header: Header<Product, unknown>;
}

export function SortableTableHeader({ id, header }: SortableTableHeaderProps) {
  // Initialize translation hook
  const i18n = useProductstableTranslations();

  const column = header.column;
  const isSorted = column.getIsSorted();

  // Don't show sort arrow on the checkbox column
  if (id === 'select') {
    return (
      <TableHead key={header.id} className="w-10 bg-muted/50 font-semibold">
        {header.isPlaceholder
          ? null
          : flexRender(header.column.columnDef.header, header.getContext())}
      </TableHead>
    );
  }

  // Special styling for action column
  if (id === 'actions') {
    return (
      <TableHead
        key={header.id}
        style={{ width: header.getSize() }}
        className="sticky right-0 z-30 min-w-[90px] whitespace-nowrap border-b border-border bg-muted/50 px-2 text-center text-sm font-semibold tracking-wide text-foreground dark:bg-muted/30"
      >
        <div className="flex items-center justify-center border-l border-border/40 pl-2">
          {header.isPlaceholder
            ? null
            : flexRender(header.column.columnDef.header, header.getContext())}
        </div>
      </TableHead>
    );
  }

  // which arrow?
  const sortIcon =
    isSorted === 'asc' ? (
      <React.Fragment>
        <ArrowUp className="ml-2 size-4" />
        <span className="sr-only">{i18n.getStateText('sortAscending')}</span>
      </React.Fragment>
    ) : isSorted === 'desc' ? (
      <React.Fragment>
        <ArrowDown className="ml-2 size-4" />
        <span className="sr-only">{i18n.getStateText('sortDescending')}</span>
      </React.Fragment>
    ) : null;

  // hide some columns on mobile
  const hideMobile = ['brand', 'barcode', 'created_at', 'tags'].includes(id)
    ? 'hidden md:table-cell'
    : '';

  return (
    <TableHead
      key={header.id}
      style={{ width: header.getSize() }}
      className={`px-2 ${hideMobile} whitespace-nowrap border-b border-border bg-muted/50 text-left text-sm font-semibold tracking-wide text-foreground dark:bg-muted/30`}
      onClick={column.getCanSort() ? column.getToggleSortingHandler() : undefined}
      aria-label={
        column.getCanSort()
          ? isSorted
            ? i18n.getStateText('sortDescending')
            : i18n.getStateText('sortAscending')
          : undefined
      }
      role={column.getCanSort() ? 'button' : undefined}
      tabIndex={column.getCanSort() ? 0 : undefined}
    >
      <div className="flex items-center">
        {header.isPlaceholder
          ? null
          : flexRender(header.column.columnDef.header, header.getContext())}
        {column.getCanSort() && sortIcon}
      </div>
    </TableHead>
  );
}
